import React, { useState } from "react";
import TopicInput from "../components/TopicInput";
import { Tab } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLearning } from "../contexts/LearningContext";

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface QuizResponse {
  completion: {
    choices: Array<{
      message: {
        content: string;
      };
    }>;
  };
}

type TabType = "summary" | "quiz" | "flashcards";

interface SummaryContent {
  emoji: string;
  title: string;
  summary: string;
}

const TAB_ICONS: Record<TabType, string> = {
  summary: "fa-book-open",
  quiz: "fa-circle-question",
  flashcards: "fa-clone",
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const { triggerLearningUpdate } = useLearning();
  const TABS: TabType[] = ["summary", "quiz", "flashcards"];
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("summary");
  const [summary, setSummary] = useState<string>("");
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [showResults, setShowResults] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [hasGeneratedQuiz, setHasGeneratedQuiz] = useState(false);
  const [hasGeneratedFlashcards, setHasGeneratedFlashcards] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "error" | "success";
  } | null>(null);
  const [currentLearningId, setCurrentLearningId] = useState<string | null>(
    null
  );
  const [quizDifficulty, setQuizDifficulty] = useState<
    "easy" | "medium" | "hard"
  >("medium");
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleTopicSubmit = async (topic: string) => {
    setIsLoading(true);
    setToast(null);
    setSummary("");
    setActiveTab("summary");
    setCurrentLearningId(null);

    try {
      const summaryResponse = await fetch(
        "https://us-central1-einstein-90036.cloudfunctions.net/generateSummaryHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic,
            userId: user?.uid,
          }),
        }
      );

      if (!summaryResponse.ok) throw new Error("Failed to generate summary");

      const summaryData = await summaryResponse.json();

      if (summaryData.learningId) {
        triggerLearningUpdate();
        navigate(`/home/learning/${summaryData.learningId}`);
        return;
      }

      const summaryContent =
        summaryData.completion.choices[0]?.message?.content;

      if (summaryContent) {
        try {
          const parsedContent: SummaryContent = JSON.parse(summaryContent);
          setSummary(JSON.stringify(parsedContent));
        } catch (e) {
          setError("Failed to parse summary content");
        }
      } else {
        throw new Error("No summary content received");
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleYouTubeSubmit = async (youtubeUrl: string) => {
    setIsLoading(true);
    setToast(null);
    setSummary("");
    setActiveTab("summary");
    setCurrentLearningId(null);

    try {
      // Fetch transcript via the dev-server proxy (setupProxy.js runs in Node.js,
      // no CORS issues, proper User-Agent forwarded to YouTube).
      const transcriptResp = await fetch(
        `/api/youtube-transcript?url=${encodeURIComponent(youtubeUrl)}`
      );
      if (!transcriptResp.ok) {
        const err = await transcriptResp.json().catch(() => ({}));
        throw new Error(err.error || "Failed to fetch YouTube transcript");
      }
      const { transcript: transcriptText } = await transcriptResp.json();
      console.log(`[youtube] transcript received: ${transcriptText.length} chars`);

      const maxChars = 24000;
      const truncated =
        transcriptText.length > maxChars
          ? transcriptText.substring(0, maxChars)
          : transcriptText;

      // Wrap in explicit instructions so the summary endpoint treats this as
      // a transcript, not a topic name, and generates a comprehensive lecture.
      const topic = `[YouTube Video Transcript]\n\n${truncated}\n\n[End Transcript]\n\nUsing the transcript above, create a comprehensive, detailed, lecture-style educational summary. Cover every key concept, example, and important point mentioned in the video. The summary should be thorough enough that a student who has not watched the video can learn the material fully.`;

      const summaryResponse = await fetch(
        "https://us-central1-einstein-90036.cloudfunctions.net/generateSummaryHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ topic, userId: user?.uid }),
        }
      );

      if (!summaryResponse.ok) {
        throw new Error("Failed to generate summary from transcript");
      }

      const data = await summaryResponse.json();

      if (data.learningId) {
        triggerLearningUpdate();
        navigate(`/home/learning/${data.learningId}`);
        return;
      }

      throw new Error("No learning content received");
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "quiz" && !hasGeneratedQuiz) {
      generateQuiz();
    } else if (tab === "flashcards" && !hasGeneratedFlashcards) {
      // coming soon
    }
  };

  const handleAnswerSelect = (optionIndex: number) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: optionIndex,
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setShowResults(true);
      setQuizSubmitted(true);
    }
  };

  const handleRestartQuiz = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setCurrentQuestionIndex(0);
  };

  const calculateScore = () => {
    return questions.reduce((score, question, index) => {
      return (
        score + (selectedAnswers[index] === question.correctAnswer ? 1 : 0)
      );
    }, 0);
  };

  const generateQuiz = async () => {
    if (!summary) {
      setToast({ message: "Please generate a summary first", type: "error" });
      setTimeout(() => setToast(null), 5000);
      return;
    }

    if (!currentLearningId) {
      setToast({
        message: "Learning document reference not found",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
      return;
    }

    setIsLoading(true);
    setToast(null);
    setQuestions([]);
    setSelectedAnswers({});
    setShowResults(false);
    setCurrentQuestionIndex(0);
    setHasGeneratedQuiz(true);

    try {
      const response = await fetch(
        "https://us-central1-einstein-90036.cloudfunctions.net/createQuizHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic: JSON.parse(summary),
            userId: user?.uid,
            learningId: currentLearningId,
            difficulty: quizDifficulty,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to generate quiz");

      const data: QuizResponse = await response.json();
      const content = data.completion.choices[0]?.message?.content;

      if (content) {
        try {
          const parsedContent = JSON.parse(content);
          setQuestions(parsedContent.questions);
        } catch (e) {
          setError("Failed to parse quiz content");
        }
      } else {
        setError("No content received from the quiz generator");
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-16 relative max-w-4xl">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-4 right-4 z-50"
          >
            <div
              className={`px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 text-sm font-medium
              ${
                toast.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              <i
                className={`fas ${
                  toast.type === "error" ? "fa-circle-xmark" : "fa-circle-check"
                }`}
              ></i>
              <span>{toast.message}</span>
              <button
                onClick={() => setToast(null)}
                className="ml-1 opacity-70 hover:opacity-100 transition-opacity"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero heading */}
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">
          What would you like to learn?
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-4 text-base sm:text-lg">
          Enter any topic and get AI-powered summaries, quizzes, and flashcards
          instantly
        </p>
      </div>

      <TopicInput onSubmit={handleTopicSubmit} onYouTubeSubmit={handleYouTubeSubmit} isLoading={isLoading} />

      {/* Feature highlights — shown only when no content has been generated */}
      {!summary && questions.length === 0 && !isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 grid grid-cols-3 gap-6"
        >
          {[
            {
              icon: "fa-book-open",
              color: "text-[#47b3fe]",
              bg: "bg-blue-50 dark:bg-blue-950/30",
              title: "AI Summary",
              desc: "Clear, concise breakdown of any topic in seconds",
            },
            {
              icon: "fa-circle-question",
              color: "text-indigo-500",
              bg: "bg-indigo-50 dark:bg-indigo-950/30",
              title: "Quiz",
              desc: "Test your understanding with generated questions",
            },
            {
              icon: "fa-clone",
              color: "text-violet-500",
              bg: "bg-violet-50 dark:bg-violet-950/30",
              title: "Flashcards",
              desc: "Reinforce knowledge with smart review cards",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center p-7 rounded-2xl bg-white dark:bg-gray-900 border border-slate-100 dark:border-gray-800 shadow-card"
            >
              <div
                className={`w-14 h-14 ${f.bg} rounded-2xl flex items-center justify-center mb-4`}
              >
                <i className={`fas ${f.icon} ${f.color} text-xl`}></i>
              </div>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1.5">
                {f.title}
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </motion.div>
      )}

      {error && (
        <div className="mt-5 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {(summary || questions.length > 0) && (
        <div className="mt-8">
          <Tab.Group onChange={(index) => handleTabChange(TABS[index])}>
            {/* Tab Bar */}
            <Tab.List className="flex p-1 gap-1 bg-slate-100 dark:bg-gray-900 rounded-2xl mb-6">
              {TABS.map((tab) => (
                <Tab
                  key={tab}
                  className={({ selected }) =>
                    `w-full py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 focus:outline-none
                    ${
                      selected
                        ? "bg-white dark:bg-gray-800 text-slate-900 dark:text-white shadow-card"
                        : "text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-400"
                    }`
                  }
                >
                  <i className={`fas ${TAB_ICONS[tab]} text-xs`}></i>
                  <span className="capitalize">{tab}</span>
                </Tab>
              ))}
            </Tab.List>

            <Tab.Panels>
              {/* Summary Panel */}
              <Tab.Panel>
                <AnimatePresence mode="wait">
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    {summary && (
                      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8">
                        <div className="flex items-center justify-center mb-6">
                          <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950/40 rounded-2xl flex items-center justify-center text-3xl">
                            {JSON.parse(summary).emoji}
                          </div>
                        </div>

                        <h2 className="text-2xl font-bold text-center mb-5 text-slate-900 dark:text-white tracking-tight">
                          {JSON.parse(summary).title}
                        </h2>

                        <div className="space-y-3">
                          {JSON.parse(summary)
                            .summary.split(". ")
                            .map((sentence: string, index: number) => (
                              <motion.p
                                key={index}
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 + index * 0.07 }}
                                className="text-base text-slate-600 dark:text-slate-300 leading-relaxed"
                              >
                                {sentence.trim() +
                                  (index <
                                  JSON.parse(summary).summary.split(". ")
                                    .length -
                                    1
                                    ? "."
                                    : "")}
                              </motion.p>
                            ))}
                        </div>

                        <div className="mt-8 pt-6 border-t border-slate-100 dark:border-gray-800">
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={() => handleTabChange("quiz")}
                              className="px-5 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors duration-200 flex items-center gap-2"
                            >
                              <i className="fas fa-circle-question text-xs"></i>
                              <span>Take Quiz</span>
                            </button>
                            <button
                              onClick={() => handleTabChange("flashcards")}
                              className="px-5 py-2.5 border border-slate-200 dark:border-gray-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800 text-sm font-medium rounded-xl transition-colors duration-200 flex items-center gap-2"
                            >
                              <i className="fas fa-clone text-xs"></i>
                              <span>Flashcards</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </Tab.Panel>

              {/* Quiz Panel */}
              <Tab.Panel>
                <AnimatePresence mode="wait">
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    {questions.length > 0 && !showResults && (
                      <div className="max-w-xl mx-auto">
                        {/* Progress */}
                        <div className="mb-5">
                          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
                            <span>
                              Question {currentQuestionIndex + 1} of{" "}
                              {questions.length}
                            </span>
                            <span>
                              {Math.round(
                                ((currentQuestionIndex + 1) /
                                  questions.length) *
                                  100
                              )}
                              %
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#47b3fe] rounded-full transition-all duration-300"
                              style={{
                                width: `${
                                  ((currentQuestionIndex + 1) /
                                    questions.length) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>

                        {/* Question card */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-6">
                          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-5 leading-snug">
                            {questions[currentQuestionIndex].question}
                          </h2>

                          <div className="space-y-2.5">
                            {questions[currentQuestionIndex].options.map(
                              (option, optionIndex) => {
                                const isSelected =
                                  selectedAnswers[currentQuestionIndex] ===
                                  optionIndex;
                                const isCorrect =
                                  optionIndex ===
                                  questions[currentQuestionIndex].correctAnswer;
                                const showResult = quizSubmitted;

                                return (
                                  <button
                                    key={optionIndex}
                                    onClick={() =>
                                      !selectedAnswers[
                                        currentQuestionIndex
                                      ] && handleAnswerSelect(optionIndex)
                                    }
                                    disabled={
                                      selectedAnswers[
                                        currentQuestionIndex
                                      ] !== undefined
                                    }
                                    className={`
                                      w-full p-4 rounded-xl text-left transition-all duration-200 text-sm border
                                      ${
                                        !showResult &&
                                        !isSelected &&
                                        "hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:border-blue-200 dark:hover:border-blue-800"
                                      }
                                      ${
                                        !isSelected && !showResult
                                          ? "border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                                          : ""
                                      }
                                      ${
                                        isSelected && isCorrect
                                          ? "bg-green-50 dark:bg-green-950/30 border-green-400 dark:border-green-700"
                                          : ""
                                      }
                                      ${
                                        isSelected && !isCorrect
                                          ? "bg-red-50 dark:bg-red-950/30 border-red-400 dark:border-red-700"
                                          : ""
                                      }
                                      ${
                                        selectedAnswers[
                                          currentQuestionIndex
                                        ] !== undefined && !isSelected
                                          ? "opacity-40"
                                          : ""
                                      }
                                    `}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-slate-800 dark:text-slate-200">
                                        {option}
                                      </span>
                                      {showResult && isSelected && (
                                        <i
                                          className={`fas ${
                                            isCorrect
                                              ? "fa-check text-green-500"
                                              : "fa-times text-red-500"
                                          } text-xs`}
                                        ></i>
                                      )}
                                    </div>
                                  </button>
                                );
                              }
                            )}
                          </div>

                          <div className="flex justify-between items-center mt-6">
                            <button
                              onClick={() =>
                                setCurrentQuestionIndex((prev) =>
                                  Math.max(0, prev - 1)
                                )
                              }
                              className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                                currentQuestionIndex > 0
                                  ? "bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-700"
                                  : "bg-slate-50 dark:bg-gray-900 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                              }`}
                              disabled={currentQuestionIndex === 0}
                            >
                              Previous
                            </button>
                            <span className="text-xs text-slate-400">
                              {currentQuestionIndex + 1} / {questions.length}
                            </span>
                            <button
                              onClick={handleNextQuestion}
                              className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                                selectedAnswers[currentQuestionIndex] !==
                                undefined
                                  ? "bg-[#47b3fe] hover:bg-blue-500 text-white"
                                  : "bg-slate-100 dark:bg-gray-800 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                              }`}
                              disabled={
                                selectedAnswers[currentQuestionIndex] ===
                                undefined
                              }
                            >
                              {currentQuestionIndex === questions.length - 1
                                ? "Submit"
                                : "Next"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {showResults && (
                      <div className="max-w-xl mx-auto bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8">
                        <h2 className="text-2xl font-bold mb-2 text-center text-slate-900 dark:text-white">
                          Quiz Complete!
                        </h2>
                        <div className="mb-7 text-center">
                          <div className="text-5xl font-bold text-[#47b3fe] mb-1">
                            {calculateScore()}/{questions.length}
                          </div>
                          <p className="text-slate-500 dark:text-slate-400 text-sm">
                            You got {calculateScore()} out of {questions.length}{" "}
                            questions correct
                          </p>
                        </div>

                        <div className="mb-7">
                          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
                            Review
                          </h3>
                          <div className="space-y-4">
                            {questions.map((question, qIndex) => (
                              <div
                                key={qIndex}
                                className="p-4 border border-slate-100 dark:border-gray-800 rounded-xl bg-slate-50 dark:bg-gray-950"
                              >
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm mb-2">
                                  {qIndex + 1}. {question.question}
                                </p>
                                <div className="space-y-1.5 pl-2">
                                  {question.options.map((option, oIndex) => {
                                    const isUserAnswer =
                                      selectedAnswers[qIndex] === oIndex;
                                    const isCorrect =
                                      question.correctAnswer === oIndex;

                                    return (
                                      <div
                                        key={oIndex}
                                        className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2
                                          ${
                                            isCorrect
                                              ? "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400"
                                              : isUserAnswer
                                              ? "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400"
                                              : "text-slate-500 dark:text-slate-600"
                                          }`}
                                      >
                                        {isCorrect && (
                                          <i className="fas fa-check text-xs"></i>
                                        )}
                                        {isUserAnswer && !isCorrect && (
                                          <i className="fas fa-times text-xs"></i>
                                        )}
                                        {option}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="text-center">
                          <button
                            onClick={handleRestartQuiz}
                            className="px-6 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </Tab.Panel>

              {/* Flashcards Panel */}
              <Tab.Panel>
                <AnimatePresence mode="wait">
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8 text-center"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-clone text-slate-400 dark:text-slate-600"></i>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                      Flashcards
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      Coming soon — flashcard practice for this topic
                    </p>
                  </motion.div>
                </AnimatePresence>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      )}
    </div>
  );
};

export default Home;
